import { POS_WS_API } from '../app';
import { IS_POS } from '@/utils/env';
import { useCallback, useEffect, useRef, useState } from 'react';

export const DOMAIN_PAYMENT = 'PAYMENT';
export const DOMAIN_POS = 'POS';
export const DOMAIN_STOCK = 'STOCK';
export const ONLINE_ORDER = 'ONLINE_ORDER';
export const DOMAIN_INTERACT_OUTSIDE = 'INTERACT_OUTSIDE';

export interface CommonTextTipMsg {
  level: 'warn' | 'success' | 'info' | 'error';
  text: string;
  key: string;
}

export enum WS_CLOSE_REASON {
  KICK_AWAY_BY_SELF = 'KICK_AWAY_BY_SELF',
  TERMINATE = 'TERMINATE',
}

export enum WsActions {
  COMMON_TEXT_TIP = 0,

  PAYING_TIP = 11000,

  POI_CONFIG_UPDATE = 10,

  POS_DATA_VERSION = 100,

  CATEGORY_ADD = 1001,
  CATEGORY_UPDATE = 1002,
  CATEGORY_DELETE = 1003,
  CATEGORY_ORDER_CHANGE = 1004,

  GOODS_ADD = 1110,
  GOODS_UPDATE = 1120,
  GOODS_UPDATE_ALL = 1121,
  GOODS_DELETE = 1130,

  STOCK_UPDATE = 1180,

  CARD_ADD = 1210,
  CARD_UPDATE = 1220,
  CARD_DELETE = 1230,

  POINT_RULE_CHANGE = 1250,

  // 小程序订单支付
  WEAPP_ORDER_PAY = 10001,

  //  小程序联动
  WEAPP_USER_VISIT_IN_POI = 20001,
}

export type WsInMsg = {
  domain: string;
  action: WsActions;
  data: unknown;
};

type Listener<T> = {
  (data: T, domain: string, action: WsActions): void | true;
};

const listeners: {
  [domainAndAction: string]: Listener<unknown>[];
} = {};

type SocketListener = {
  open: () => void;
  close: () => void;
  error: () => void;
  pong: (delay: number) => void;
};

const socketListeners: {
  open: SocketListener['open'][];
  close: SocketListener['close'][];
  error: SocketListener['error'][];
  pong: SocketListener['pong'][];
} = {
  open: [],
  close: [],
  error: [],
  pong: [],
};

export const addSocketListener = (type: keyof SocketListener, listener: (...args) => void) => {
  socketListeners[type].push(listener);
  return `${type}-${socketListeners[type].length - 1}`;
};

export const removeSocketListener = (key: string) => {
  const [type, index] = key.split('-');
  socketListeners[type].splice(index, 1);
};

const triggerSocketListener = (type: keyof SocketListener, ...args) => {
  const listeners = socketListeners[type];
  for (let listener of listeners) {
    // @ts-ignore
    listener(...args);
  }
};

let socket: WebSocket;
let wsPingPongDelay: number;
let isConnecting = false;
const createSocketConnection = () => {
  if (isConnecting || (socket && socket.readyState === WebSocket.OPEN)) {
    return;
  }

  // 熄屏状态不连接
  if (IS_POS && window.__POS_Device__.isScreenOff?.() === true) {
    setTimeout(() => {
      createSocketConnection();
    }, 1000);
    return;
  }

  isConnecting = true;
  socket = new WebSocket(`${POS_WS_API}/websocket`);

  let heartbeatInterval;

  let disconnectTimer;
  let timePing;

  // Connection opened
  socket.addEventListener('open', function (event) {
    isConnecting = false;

    triggerSocketListener('open');

    const heartBeat = () => {
      timePing = new Date().getTime();
      if (socket.readyState !== WebSocket.OPEN) {
        socket.close();
        return;
      }

      socket.send('ping');
      disconnectTimer = setTimeout(() => {
        socket.close();
      }, 5e3);
    };
    heartBeat();
    heartbeatInterval = setInterval(heartBeat, 6e3);
  });

  // Listen for messages
  socket.addEventListener('message', function (event) {
    if (event && event.data === 'pong') {
      // 心跳检测
      triggerSocketListener('pong', (wsPingPongDelay = new Date().getTime() - timePing));
      clearTimeout(disconnectTimer);
      return;
    }
    try {
      const msg = JSON.parse(event.data) as WsInMsg;
      const listenerKey = `${msg.domain}-${msg.action}`;
      const domainWildcardKey = `${msg.domain}-*`;

      if (listeners.hasOwnProperty(listenerKey) && listeners[listenerKey].length) {
        for (let i = listeners[listenerKey].length - 1; i >= 0; i--) {
          const listener = listeners[listenerKey][i];
          // 可能为 null
          if (listener) {
            try {
              const stopPropagation = listener(msg.data, msg.domain, msg.action);
              // 阻止事件传播
              if (stopPropagation === true) {
                break;
              }
            } catch (e) {
              console.error('ws listener 执行异常: ', e);
            }
          }
        }
      } else if (listeners.hasOwnProperty(domainWildcardKey) && listeners[domainWildcardKey].length) {
        for (let i = listeners[domainWildcardKey].length - 1; i >= 0; i--) {
          const listener = listeners[domainWildcardKey][i];
          if (listener) {
            try {
              const stopPropagation = listener(msg.data, msg.domain, msg.action);
              // 阻止事件传播
              if (stopPropagation === true) {
                break;
              }
            } catch (e) {
              console.error('ws wildcard listener 执行异常: ', e);
            }
          }
        }
      } else {
        console.warn('ws 消息分发失败: 未监听', '原始信息' + event.data);
      }
    } catch (e) {
      console.error('ws 消息分发失败: ', e, `原始信息: ${event.data}`);
    }
  });

  socket.addEventListener('close', function (event) {
    isConnecting = false;
    socket = null;
    clearInterval(heartbeatInterval);
    clearTimeout(disconnectTimer);

    triggerSocketListener('close', {
      reason: event.reason,
    });

    if (event.reason === WS_CLOSE_REASON.KICK_AWAY_BY_SELF || event.reason === WS_CLOSE_REASON.TERMINATE) {
      return;
    }

    setTimeout(() => {
      createSocketConnection();
    }, 1000);
    console.log(`ws 断开, 1s 后尝试重新连接`);
  });

  socket.addEventListener('error', function (event) {
    isConnecting = false;
    clearInterval(heartbeatInterval);
    clearTimeout(disconnectTimer);
    if (socket) {
      socket.close();
    }
    triggerSocketListener('error');
  });
};

/**
 * 连接状态监控
 */
export const useWsStatusMonitor = () => {
  const mounted = useRef(true);
  const [connected, setConnected] = useState(!!socket);
  const [delay, setDelay] = useState<number>(wsPingPongDelay);
  useEffect(() => {
    const open = addSocketListener('open', () => {
      setConnected(true);
    });
    const close = addSocketListener('close', ({ reason }) => {
      setConnected(false);
    });
    const error = addSocketListener('error', () => {
      if (mounted) {
        setConnected(false);
      }
    });
    const pong = addSocketListener('pong', delay => {
      if (mounted) {
        setDelay(delay);
      }
    });

    return () => {
      removeSocketListener(open);
      removeSocketListener(close);
      removeSocketListener(error);
      removeSocketListener(pong);
      mounted.current = false;
    };
  }, []);

  return {
    delay,
    connected,
  };
};

/**
 * 初始化
 */
export const useWsConnection = ({
  onKickAway = () => {},
}: {
  onKickAway?: (reason: WS_CLOSE_REASON) => void;
} = {}) => {
  const mounted = useRef(true);

  // 重连
  const connect = useCallback(() => {
    createSocketConnection();
  }, []);

  // 主动断开 (不会自动重连)
  const terminate = () => {
    if (socket) {
      socket.close(1000, WS_CLOSE_REASON.TERMINATE);
    }
  };

  useEffect(() => {
    const close = addSocketListener('close', ({ reason }) => {
      if (reason === WS_CLOSE_REASON.KICK_AWAY_BY_SELF) {
        if (mounted) {
          onKickAway(WS_CLOSE_REASON.KICK_AWAY_BY_SELF);
        }
      }
    });
    connect();
    return () => {
      removeSocketListener(close);
      mounted.current = false;
    };
  }, []);

  return {
    reconnect: connect,
    terminate,
  };
};

/**
 * 添加 ws 消息监听器
 * @param domain
 * @param action
 * @param listener
 */
export const addWsListener: <T>(
  domain: WsInMsg['domain'],
  action: WsInMsg['action'] | '*',
  listener: Listener<T>
) => string = (domain, action, listener) => {
  const key = `${domain}-${action}`;
  if (!listeners.hasOwnProperty(key)) {
    listeners[key] = [];
  }
  listeners[key].push(listener);
  return `${domain}-${action}-${listeners[key].length - 1}`;
};

export const removeWsListener = (id: string) => {
  const [domain, action, index] = id.split('-');
  try {
    listeners[`${domain}-${action}`][+index] = null;
  } catch (e) {}
};

export interface PosChangeIncomingMsg<T> {
  modifier?: string;
  version: number;
  payload: T;
  time: string;
}

export const addPOSChangeListener = <T>(listener: Listener<PosChangeIncomingMsg<T>>) => {
  return addWsListener<PosChangeIncomingMsg<T>>(DOMAIN_POS, '*', listener);
};
